import { Link } from "react-router-dom";
import { Layout } from "@/components/Layout";
import {
  ArrowRight,
  TreeDeciduous,
  Trash2,
  Flame,
  Footprints,
  Leaf,
  Wallet,
  Building2,
  Sprout,
  AlertOctagon,
  ShieldCheck,
  Store,
  Mountain,
  Sparkles,
  HandHeart,
  UserPlus,
  AlertTriangle,
  CheckCircle2,
  ArrowDown,
} from "lucide-react";

const PROBLEMS = [
  { icon: TreeDeciduous, title: "Uliveti abbandonati o danneggiati" },
  { icon: Trash2, title: "Biomassa e materiale bruciato accumulato" },
  { icon: Flame, title: "Aumento del rischio incendi" },
  { icon: Footprints, title: "Sentieri non percorribili" },
  { icon: Leaf, title: "Perdita di biodiversità" },
  { icon: Wallet, title: "Difficoltà economiche per molti proprietari" },
  { icon: Building2, title: "Progressivo abbandono delle attività agricole tradizionali" },
  { icon: Sprout, title: "Scarsa valorizzazione delle produzioni locali" },
];

const ROADMAP = [
  {
    label: "Fase 1",
    title: "Emergenza e ripartenza",
    icon: AlertOctagon,
    color: "from-[#C15C3D] to-[#8C3F26]",
    accent: "#C15C3D",
    items: [
      "Pulizia terreni colpiti dall'incendio",
      "Supporto diretto ai proprietari",
      "Rimozione biomassa e materiale bruciato",
      "Giornate di volontariato sul territorio",
    ],
    status: "Prossimo passo",
  },
  {
    label: "Fase 2",
    title: "Recupero del territorio",
    icon: ShieldCheck,
    color: "from-[#C19A3D] to-[#8E7129]",
    accent: "#C19A3D",
    items: [
      "Censimento uliveti",
      "Mappatura criticità ambientali",
      "Recupero dei sentieri",
      "Monitoraggio ambientale continuo",
    ],
    status: "In pianificazione",
  },
  {
    label: "Fase 3",
    title: "Valorizzazione delle produzioni locali",
    icon: Sparkles,
    color: "from-[#7BA577] to-[#456C45]",
    accent: "#7BA577",
    items: [
      "Coinvolgimento dei produttori del territorio",
      "Creazione di una rete territoriale",
      "Eventi di promozione e degustazione",
    ],
    status: "In pianificazione",
  },
  {
    label: "Fase 4",
    title: "Economia locale",
    icon: Store,
    color: "from-[#2E4F3B] to-[#1f3828]",
    accent: "#2E4F3B",
    description:
      "L'associazione sta studiando soluzioni concrete e su misura, costruite intorno alle esigenze reali di chi vuole vivere, lavorare e investire in questo territorio. L'obiettivo è creare valore per la comunità locale, valorizzando le competenze, le tradizioni e le produzioni che rendono unico il Monte Faeta.",
    status: "In studio",
  },
  {
    label: "Fase 5",
    title: "Visione futura",
    icon: Mountain,
    color: "from-[#1f3828] to-[#0f1a14]",
    accent: "#1f3828",
    items: [
      "Recupero del paesaggio storico",
      "Nuove opportunità per i giovani",
      "Turismo sostenibile",
      "Comunità più forte e resiliente",
    ],
    status: "Orizzonte",
  },
];

export default function ProgettoFaetaPage() {
  return (
    <Layout>
      {/* HERO */}
      <section
        data-testid="faeta-hero"
        className="relative min-h-[80vh] flex items-end overflow-hidden bg-[#1A1A1A]"
      >
        <img
          src="/fire-asciano.jpg"
          alt="Monte Pisano dopo l'incendio"
          className="absolute inset-0 w-full h-full object-cover opacity-55"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-[#0f1a14]" />
        <div className="pointer-events-none absolute -bottom-32 -right-32 w-[700px] h-[700px] rounded-full bg-[#C15C3D]/[0.18] blur-3xl" />

        <div className="relative max-w-6xl mx-auto px-6 sm:px-8 lg:px-12 py-24 sm:py-32 text-white">
          <Link
            to="/progetti"
            className="inline-flex items-center gap-2 text-xs sm:text-sm text-white/70 hover:text-white mb-7 transition-colors"
          >
            ← Torna ai progetti
          </Link>
          <div className="inline-flex items-center gap-2 rounded-full bg-white/10 border border-white/25 backdrop-blur px-4 py-1.5 mb-6">
            <Mountain className="w-3.5 h-3.5 text-[#E8C9A0]" />
            <span className="text-[10px] sm:text-xs font-bold uppercase tracking-[0.32em] text-[#E8C9A0]">
              Il progetto bandiera dell'associazione
            </span>
          </div>
          <h1
            data-testid="faeta-title"
            className="font-display text-5xl sm:text-7xl lg:text-8xl font-black leading-[0.9] tracking-tight"
          >
            Progetto
            <br />
            <span className="italic font-light text-[#E8C9A0]">Faeta.</span>
          </h1>
          <p className="mt-7 font-display text-xl sm:text-2xl lg:text-3xl font-light italic text-white/90 max-w-3xl leading-snug">
            Dalla ricostruzione alla rinascita del territorio.
          </p>
          <p className="mt-7 text-base sm:text-lg text-white/80 max-w-2xl leading-relaxed">
            L'incendio che ha colpito il Monte Faeta ha lasciato ferite
            profonde nel paesaggio, negli uliveti e nelle persone che vivono
            questo territorio.
          </p>
          <p className="mt-4 text-base sm:text-lg text-white/80 max-w-2xl leading-relaxed">
            Il <strong className="text-white">Progetto Faeta</strong> nasce con una
            visione ambiziosa: trasformare una tragedia in un'opportunità di
            rinascita, creando un percorso concreto che unisca tutela
            ambientale, recupero del paesaggio, valorizzazione delle produzioni
            locali e sviluppo di nuove opportunità per la comunità.
          </p>

          <div className="mt-10 inline-flex items-center gap-2 text-sm text-[#E8C9A0]">
            <ArrowDown className="w-4 h-4 animate-bounce" />
            Scorri per scoprire il progetto
          </div>
        </div>
      </section>

      {/* PERCHÉ NASCE IL PROGETTO */}
      <section
        data-testid="faeta-problems"
        className="py-20 sm:py-28 bg-[#FBF9F6]"
      >
        <div className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="max-w-3xl mb-12 sm:mb-16">
            <div className="flex items-center gap-3 mb-5">
              <span className="w-10 h-px bg-[#C15C3D]" />
              <span className="text-[10px] sm:text-xs font-bold uppercase tracking-[0.32em] text-[#C15C3D]">
                Perché nasce il progetto
              </span>
            </div>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black text-[#1A1A1A] leading-[0.95] tracking-tight">
              Le ferite del
              <br />
              <span className="italic font-light text-[#2E4F3B]">nostro territorio.</span>
            </h2>
            <p className="mt-6 text-base sm:text-lg text-[#5C5C5C] leading-relaxed">
              Dopo l'incendio, il Monte Faeta e i terreni circostanti si trovano
              ad affrontare problemi concreti che richiedono interventi mirati e
              coordinati.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
            {PROBLEMS.map((p, i) => {
              const Icon = p.icon;
              return (
                <div
                  key={p.title}
                  data-testid={`faeta-problem-${i}`}
                  className="group flex flex-col gap-4 p-6 sm:p-7 rounded-2xl bg-white border-2 border-[#E5E1D8] hover:border-[#C15C3D] transition-all hover:-translate-y-1 hover:shadow-[0_15px_40px_rgba(0,0,0,0.06)]"
                >
                  <div className="w-12 h-12 rounded-xl bg-[#C15C3D]/10 text-[#C15C3D] flex items-center justify-center">
                    <Icon className="w-6 h-6" strokeWidth={1.6} />
                  </div>
                  <h3 className="font-display text-base sm:text-lg font-black text-[#1A1A1A] leading-snug">
                    {p.title}
                  </h3>
                </div>
              );
            })}
          </div>

          {/* Closing quote */}
          <div className="mt-16 sm:mt-20 text-center">
            <div className="inline-block max-w-3xl">
              <div className="text-5xl sm:text-7xl font-display font-black text-[#C15C3D] leading-none mb-4 select-none">
                "
              </div>
              <p className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold text-[#1A1A1A] leading-tight tracking-tight">
                Il territorio non ha bisogno soltanto di essere{" "}
                <span className="italic font-light text-[#2E4F3B]">ripulito</span>.{" "}
                <br className="hidden sm:block" />
                Ha bisogno di essere{" "}
                <span className="italic font-light text-[#C15C3D]">rimesso in moto</span>.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* SEZIONE EVIDENZIATA: PRIMO PASSO */}
      <section
        data-testid="faeta-priority"
        className="relative py-20 sm:py-28 bg-gradient-to-br from-[#C15C3D] via-[#a64830] to-[#8C3F26] text-white overflow-hidden"
      >
        <div className="pointer-events-none absolute -top-32 -right-32 w-[600px] h-[600px] rounded-full bg-[#E8C9A0]/[0.15] blur-3xl" />
        <div className="pointer-events-none absolute -bottom-32 -left-32 w-[500px] h-[500px] rounded-full bg-white/[0.08] blur-3xl" />

        <div className="relative max-w-5xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="inline-flex items-center gap-2 rounded-full bg-white/15 border border-white/25 backdrop-blur px-4 py-1.5 mb-6">
            <AlertTriangle className="w-3.5 h-3.5" />
            <span className="text-[10px] sm:text-xs font-bold uppercase tracking-[0.32em]">
              Priorità immediata
            </span>
          </div>

          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black leading-[0.95] tracking-tight max-w-3xl">
            Il primo passo:
            <br />
            <span className="italic font-light text-[#E8C9A0]">aiutare chi ha subito i danni.</span>
          </h2>

          <div className="mt-10 grid lg:grid-cols-5 gap-8 sm:gap-12">
            <div className="lg:col-span-3 space-y-5 text-base sm:text-lg text-white/90 leading-relaxed">
              <p>
                Prima di parlare di progetti futuri, mercati locali o nuove
                opportunità economiche, esiste una priorità immediata.
              </p>
              <p>
                Molti proprietari si trovano oggi con uliveti e terreni
                danneggiati dall'incendio, pieni di materiale da rimuovere,
                alberi caduti e potature da smaltire.
              </p>
              <p>
                Per questo motivo il <strong className="text-white">primo intervento del
                Progetto Faeta</strong> sarà l'organizzazione di una o più giornate di
                volontariato dedicate alla pulizia e alla messa in sicurezza
                dei terreni colpiti.
              </p>
              <p className="text-white/85 italic">
                L'obiettivo è fornire un aiuto concreto a chi da solo non
                riesce ad affrontare il lavoro necessario per ripartire.
              </p>
            </div>

            <div className="lg:col-span-2">
              <div className="bg-white text-[#1A1A1A] rounded-3xl p-7 shadow-2xl space-y-3">
                <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#C15C3D] mb-2">
                  Come puoi aiutare
                </div>
                <Link
                  to="/partecipa"
                  data-testid="faeta-cta-volontario"
                  className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#C15C3D] hover:bg-[#a64830] text-white font-bold text-sm sm:text-base px-5 py-3.5 transition-colors shadow-md"
                >
                  <UserPlus className="w-4 h-4" />
                  Partecipa come volontario
                  <ArrowRight className="w-4 h-4" />
                </Link>
                <Link
                  to="/aiuto-pulizia"
                  data-testid="faeta-cta-segnala"
                  className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#1A1A1A] hover:bg-[#2E4F3B] text-white font-bold text-sm sm:text-base px-5 py-3.5 transition-colors"
                >
                  <HandHeart className="w-4 h-4" />
                  Segnala un terreno
                  <ArrowRight className="w-4 h-4" />
                </Link>
                <p className="pt-2 text-[11px] text-[#5C5C5C] text-center leading-relaxed">
                  Hai un terreno colpito? Compila il modulo, ti contatteremo per
                  organizzare l'intervento.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ROADMAP — TIMELINE */}
      <section
        data-testid="faeta-roadmap"
        className="py-20 sm:py-28 bg-[#FBF9F6]"
      >
        <div className="max-w-5xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="text-center max-w-2xl mx-auto mb-14 sm:mb-20">
            <div className="flex items-center justify-center gap-3 mb-5">
              <span className="w-10 h-px bg-[#C15C3D]" />
              <span className="text-[10px] sm:text-xs font-bold uppercase tracking-[0.32em] text-[#C15C3D]">
                Roadmap del progetto
              </span>
              <span className="w-10 h-px bg-[#C15C3D]" />
            </div>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black text-[#1A1A1A] leading-[0.95] tracking-tight">
              Cinque fasi per
              <br />
              <span className="italic font-light text-[#2E4F3B]">rimettere in moto il territorio.</span>
            </h2>
          </div>

          {/* Timeline */}
          <div className="relative">
            {/* Vertical line */}
            <div className="absolute left-6 sm:left-1/2 sm:-translate-x-1/2 top-0 bottom-0 w-0.5 bg-[#E5E1D8]" />

            {ROADMAP.map((phase, idx) => {
              const Icon = phase.icon;
              const isRight = idx % 2 === 1;
              return (
                <article
                  key={phase.label}
                  data-testid={`faeta-phase-${idx}`}
                  className={`relative flex gap-5 sm:gap-10 mb-10 sm:mb-16 last:mb-0 ${isRight ? "sm:flex-row-reverse" : ""}`}
                >
                  {/* Connector dot */}
                  <div
                    className={`absolute left-6 sm:left-1/2 -translate-x-1/2 top-6 w-5 h-5 rounded-full border-4 border-[#FBF9F6] z-10 bg-gradient-to-br ${phase.color}`}
                  />

                  {/* Card */}
                  <div className="ml-16 sm:ml-0 sm:w-1/2 sm:px-6">
                    <div className="rounded-2xl bg-white border-2 border-[#E5E1D8] p-7 sm:p-8 hover:border-[#2E4F3B] transition-colors shadow-sm">
                      <div className="flex items-center gap-3 mb-4">
                        <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${phase.color} flex items-center justify-center text-white shrink-0`}>
                          <Icon className="w-5 h-5" strokeWidth={1.7} />
                        </div>
                        <div>
                          <div className="text-[10px] font-bold uppercase tracking-[0.22em]" style={{ color: phase.accent }}>
                            {phase.label} · {phase.status}
                          </div>
                          <h3 className="font-display text-xl sm:text-2xl font-black text-[#1A1A1A] leading-tight">
                            {phase.title}
                          </h3>
                        </div>
                      </div>
                      <ul className="space-y-2.5 mt-5">
                        {(phase.items || []).map((item) => (
                          <li key={item} className="flex items-start gap-3 text-sm sm:text-base text-[#3F3F3F]">
                            <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" style={{ color: phase.accent }} />
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                      {phase.description && (
                        <p className="mt-5 text-sm sm:text-base text-[#3F3F3F] leading-relaxed">
                          {phase.description}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Spacer half on desktop */}
                  <div className="hidden sm:block sm:w-1/2" />
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* VISIONE FINALE */}
      <section
        data-testid="faeta-vision"
        className="relative py-20 sm:py-28 bg-[#1f2d22] text-white overflow-hidden"
      >
        <img
          src="/hero-asciano.jpg"
          alt="Paesaggio del Monte Faeta"
          className="absolute inset-0 w-full h-full object-cover opacity-25"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#1f2d22]/85 via-[#1f2d22]/90 to-[#0f1a14]" />
        <div className="pointer-events-none absolute -top-32 -right-32 w-[600px] h-[600px] rounded-full bg-[#E8C9A0]/[0.08] blur-3xl" />

        <div className="relative max-w-4xl mx-auto px-6 sm:px-8 lg:px-12 text-center">
          <div className="inline-flex items-center gap-2 rounded-full bg-white/10 border border-white/20 backdrop-blur px-4 py-1.5 mb-6">
            <Sparkles className="w-3.5 h-3.5 text-[#E8C9A0]" />
            <span className="text-[10px] sm:text-xs font-bold uppercase tracking-[0.32em] text-[#E8C9A0]">
              La visione finale
            </span>
          </div>

          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black leading-[0.95] tracking-tight">
            Una rinascita
            <br />
            <span className="italic font-light text-[#E8C9A0]">che parte dal territorio.</span>
          </h2>

          <div className="mt-10 space-y-5 text-lg sm:text-xl text-white/90 leading-relaxed">
            <p>
              Il Progetto Faeta non vuole limitarsi a rispondere all'emergenza.
            </p>
            <p>
              Vuole costruire un <strong className="text-white">modello di rinascita
              territoriale</strong> che parta dalle persone, dagli uliveti, dai
              sentieri e dalle tradizioni locali per creare nuove opportunità
              economiche e sociali.
            </p>
            <p className="text-[#E8C9A0] italic font-display text-2xl sm:text-3xl pt-3">
              Ogni terreno recuperato, ogni sentiero riaperto e ogni produttore
              valorizzato rappresentano un passo verso un territorio più
              sicuro, più vivo e più forte.
            </p>
          </div>

          <div className="mt-12 flex flex-col sm:flex-row gap-3 justify-center">
            <Link
              to="/partecipa"
              data-testid="faeta-vision-cta-volontari"
              className="inline-flex items-center justify-center gap-2 rounded-full bg-[#C15C3D] hover:bg-[#a64830] text-white font-bold px-7 py-4 transition-colors shadow-lg"
            >
              <UserPlus className="w-4 h-4" />
              Unisciti come volontario
              <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/associarsi"
              data-testid="faeta-vision-cta-soci"
              className="inline-flex items-center justify-center gap-2 rounded-full bg-white text-[#1A1A1A] hover:bg-[#E8C9A0] font-bold px-7 py-4 transition-colors"
            >
              Diventa socio
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>
    </Layout>
  );
}
